import { useContentLink, usePageContent } from "@robe/client/xcore-ui";
import { SubCategory } from "@robe/core";
import { OptionType } from "design-system/xcore/Select";
import { useSubCategoryFilter } from "hooks";
import { complement, isNil, prop } from "ramda";
import { useCallback, useMemo } from "react";
import { OnChangeValue } from "react-select";
import { useLayout } from "xcore";

const useProductFilter = (name: string, categories: SubCategory[], pageCategory?: SubCategory) => {
  const { stringify } = useLayout();
  const validCategories = useMemo(() => categories.filter((c) => !!c.content), [categories]);
  const filter = useSubCategoryFilter(name, validCategories);

  const value = useMemo(
    () =>
      pageCategory && pageCategory.type === name
        ? [{ label: stringify(pageCategory.name), value: pageCategory.id }, ...filter[0].value]
        : filter[0].value,
    [filter, name, pageCategory, stringify]
  );

  const options = useMemo(() => filter[0].options.filter((c) => !value.find((v) => v.value === c.value)), [filter, value]);

  const onChange = useCallback(
    (q: string, val: OnChangeValue<OptionType, boolean>): number[] => {
      const newValues = q === name ? [val].flat() : (value as any); // as any because of Ramda typing
      return toSet(newValues.filter(complement(isNil)).map(prop("value")));
    },
    [name, value]
  );

  const getQuery = (v: number[]) => ({ [name]: v });

  return {
    value,
    options,
    onChange,
    getQuery,
  };
};

export const useProductFilters = () => {
  const { push } = useContentLink();
  const pop = usePageContent();
  const {
    header: {
      nav: { products },
    },
    subCategories,
  } = useLayout();

  const allCategories = useMemo(() => subCategories.slice(0, 3).flat(), [subCategories]);

  const pageCategory = useMemo(() => (pop ? allCategories.find((c) => c.contentId === pop.id) : undefined), [allCategories, pop]);

  const productType = useProductFilter("product-type", subCategories[0], pageCategory);
  const productSize = useProductFilter("product-size", subCategories[1], pageCategory);
  const beamType = useProductFilter("beam-type", subCategories[2], pageCategory);

  const onChange = (q: string) => (val: OnChangeValue<OptionType, boolean>) => {
    const productTypeValues = productType.onChange(q, val);
    const productSizeValues = productSize.onChange(q, val);
    const beamTypeValues = beamType.onChange(q, val);

    const queryParamsCount = productTypeValues.length + productSizeValues.length + beamTypeValues.length;

    if (queryParamsCount === 0) {
      push(products);
    } else if (queryParamsCount === 1) {
      const category = [...productTypeValues, ...productSizeValues, ...beamTypeValues][0];
      const { content } = allCategories.find((c) => c.id === category) as SubCategory;

      if (content) {
        push(content);
      }
    } else {
      push(products, {
        queries: {
          ...productType.getQuery(productTypeValues),
          ...productSize.getQuery(productSizeValues),
          ...beamType.getQuery(beamTypeValues),
        },
      });
    }
  };

  return {
    values: {
      "product-type": productType.value,
      "product-size": productSize.value,
      "beam-type": beamType.value,
    },
    options: {
      "product-type": productType.options,
      "product-size": productSize.options,
      "beam-type": beamType.options,
    },
    onChange,
    subCategories: subCategories.flat(), // To match subCategories with the filter type
  };
};

const toSet = <T extends unknown[]>(source: T) => [...new Set(source)] as T;
